import Label from "src/components/atoms/labels/label";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import Banner from "src/components/molecules/banner/Banner";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-context";
import {
	AccountManagementViewModel,
	IAccountModel,
	IInviteUserResponse,
} from "./account-management-view-model";
import Alert from "src/components/atoms/alert/alert";
import { TUserScope } from "../invite-user/invite-user.view-model";
import { IContactModel, IUserScope } from "src/types/models/apiModels";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import ProductPlanMarker, {
	TProductPlan,
} from "src/components/molecules/product-plan-marker/product-plan-marker";
import { ReactComponent as AddUserIcon } from "../../../assets/svg/addUserIcon.svg";
import { ReactComponent as AccountIcon } from "../../../assets/svg/accountIcon.svg";
import Divider from "src/components/atoms/divider/divider";
import { Dialog, DialogContent } from "@mui/material";
import ListUsers from "./list-users";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import LoadingModal from "src/components/molecules/loading/LoadingModal";

const AccountManagement = () => {
	const [emailId, setEmailId] = useState("");
	const [originalAccountName, setOriginalAccountName] =
		useState("Unnamed account");
	const [originalAccountOwnerId, setOriginalAccountOwnerId] = useState("");
	const [accountName, setAccountName] = useState(originalAccountName);
	const [accountOwnerId, setAccountOwnerId] = useState("");
	const [accountOwnerFirstName, setAccountOwnerFirstName] = useState("");
	const [accountOwnerLastName, setAccountOwnerLastName] = useState("");
	const [accountOwnerEmailAddress, setAccountOwnerEmailAddress] =
		useState("");
	const [accountProductPlan, setAccountProductPlan] =
		useState<TProductPlan>("Basic");
	const [showChangeOwnerDialog, setShowChangeOwnerDialog] = useState(false);
	const [sending, setSending] = useState(false);
	const [loading, setLoading] = useState(false);
	const [enableSubmit, setEnableSubmit] = useState(false);
	const [accountLevelUserScope, setAccountLevelUserScope] = useState<
		IUserScope[]
	>([]);
	const [result, setResult] = useState<IInviteUserResponse>();
	const [canUpdateAccount, setCanUpdateAccount] = useState(false);
	const [canInviteMembers, setCanInviteMembers] = useState(false);
	const { user } = useAuth();
	const viewModel = useRef<AccountManagementViewModel | null>(null);

	const getScopes = async (level: TUserScope) => {
		if (viewModel.current) {
			const scopesResponse =
				await viewModel.current.fetchUserScopesAsync(level);
			if (scopesResponse) {
				if (level === "Account") {
					setAccountLevelUserScope(scopesResponse);
				}
			}
		}
	};

	useEffect(() => {
		setEmailId("");
		setSending(false);

		if (user && user.accessToken) {
			viewModel.current = new AccountManagementViewModel(
				user.accessToken
			);
		}

		if (user) {
			setCanUpdateAccount(
				user.permissionBag.permissions.filter(
					(p) => p.name === "Account" && p.value === "Edit"
				).length > 0
			);

			setCanInviteMembers(
				user.permissionBag.permissions.filter(
					(p) => p.name === "AccountContact" && p.value === "Create"
				).length > 0
			);
		}

		const fetchAccount = async () => {
			if (user && user.accessToken) {
				setLoading(true);
				viewModel.current = new AccountManagementViewModel(
					user?.accessToken
				);
				const account: IAccountModel =
					await viewModel.current.getAccountAsync();

				setOriginalAccountName(account.name);
				setOriginalAccountOwnerId(account.rootUser.userId ?? "");
				setAccountOwnerId(account.rootUser.userId ?? "");
				setAccountName(account.name ?? originalAccountName);
				setAccountOwnerFirstName(account.rootUser.firstName);
				setAccountOwnerLastName(account.rootUser.lastName);
				setAccountOwnerEmailAddress(account.rootUser.emailId);
				setAccountProductPlan(account.productPlan ?? "Basic");
				await getScopes("Account");
				setLoading(false);
			}
		};

		fetchAccount();
	}, []);

	const handleInviteUser = async () => {
		setSending(true);

		const adminRole = accountLevelUserScope.find(
			(p) => p.key === "AccountFullAccess"
		);

		if (adminRole) {
			const result = await viewModel.current?.inviteUserAsync(
				emailId,
				adminRole.id
			);

			setResult(result);
			setEmailId("");
		}
		setSending(false);
	};

	const handleSubmitAccountUpdate = async () => {
		if (viewModel.current) {
			setSending(true);
			const result = await viewModel.current.submitAccountUpdateAsync(
				accountName,
				accountOwnerEmailAddress
			);

			if (result) {
				setOriginalAccountName(accountName);
				setOriginalAccountOwnerId(accountOwnerId);
				setEnableSubmit(false);
			}

			setSending(false);
		}
	};

	const toggleShowChangeOwnerDialog = () => {
		setShowChangeOwnerDialog((prevValue) => !prevValue);
	};

	return (
		<Banner label="Account management">
			<div className="flex flex-col items-start justify-center gap-8 p-4">
				<BoxWithIcon
					icon={<AccountIcon className="h-4 w-4"></AccountIcon>}
					title={"Account information"}
				>
					<div className="flex flex-col items-start justify-center gap-8 p-4">
						<InputBox
							label={"Account name"}
							type={"text"}
							value={accountName}
							onChange={function (value: string): void {
								setAccountName(value);
							}}
							readOnly={!canUpdateAccount}
						></InputBox>
						<div className="flex items-center justify-center gap-4">
							<span className="font-Poppins font-semibold text-md text-gray-900 tracking-wider">
								Product Plan
							</span>
							<ProductPlanMarker
								plan={accountProductPlan}
							></ProductPlanMarker>
						</div>
						<Divider></Divider>
					</div>
					<div className="flex flex-col items-start justify-center gap-8 p-4 w-full">
						<div className="flex items-center justify-start gap-2 w-full">
							<Label
								variant="subheader"
								text={"Account owner"}
							></Label>

							{user?.id === originalAccountOwnerId && (
								<HyperLink
									onClick={toggleShowChangeOwnerDialog}
									href={"#"}
									label={"[change]"}
									disabled={!canUpdateAccount}
								></HyperLink>
							)}
						</div>
						<div className="flex items-start justify-center gap-8 w-full">
							<InputBox
								label={"First name"}
								type={"text"}
								required
								readOnly
								disabled
								value={accountOwnerFirstName}
								onChange={function (value: string): void {
									setAccountOwnerFirstName(value);
								}}
							></InputBox>
							<InputBox
								label={"Last name"}
								type={"text"}
								required
								readOnly
								disabled
								value={accountOwnerLastName}
								onChange={function (value: string): void {
									setAccountOwnerLastName(value);
								}}
							></InputBox>
						</div>
						<div className="flex items-start justify-center gap-8 w-full">
							<InputBox
								label={"Email address"}
								type={"text"}
								required
								readOnly
								disabled
								value={accountOwnerEmailAddress}
								onChange={function (value: string): void {
									setAccountOwnerEmailAddress(value);
								}}
							></InputBox>
							{/* <InputBox
								label={"Phone number"}
								type={"tel"}
								required
								readOnly
								value={accountOwnerMobileNumber}
								onChange={function (value: string): void {
									setAccountOwnerMobileNumber(value);
								}}
							></InputBox> */}
						</div>
						<ButtonPro
							disabled={
								!canUpdateAccount ||
								(originalAccountName === accountName &&
									originalAccountOwnerId === accountOwnerId)
							}
							label={`Update`}
							onClick={handleSubmitAccountUpdate}
						/>
					</div>
				</BoxWithIcon>
				<BoxWithIcon
					icon={<AddUserIcon className="h-4 w-4"></AddUserIcon>}
					title={"Invite user"}
				>
					<div className="flex flex-col items-start justify-center gap-8 p-4">
						<InputBox
							label={"Business email address"}
							type={"email"}
							value={emailId}
							onChange={function (value: string): void {
								setEmailId(value);
							}}
							disabled={!canInviteMembers}
						></InputBox>

						<ButtonPro
							disabled={!canInviteMembers || !emailId}
							onClick={handleInviteUser}
							label={"Invite"}
						></ButtonPro>

						{result && result.success && (
							<Alert
								text={result.message}
								type={"success"}
							></Alert>
						)}

						{result && !result.success && (
							<Alert text={result.message} type={"error"}></Alert>
						)}
					</div>
				</BoxWithIcon>
			</div>
			<LoadingModal
				open={sending || loading}
				message="Loading..."
			></LoadingModal>
			{/* showContactCenterMandatoryOption */}
			<Dialog
				open={showChangeOwnerDialog}
				onClose={toggleShowChangeOwnerDialog}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner label={"Select new account owner"}>
						<ListUsers
							onCancel={toggleShowChangeOwnerDialog}
							userIdsToIgnore={[originalAccountOwnerId]}
							onSelect={function (
								selectedUser: IContactModel
							): void {
								setAccountOwnerId(selectedUser.userId ?? "");
								setAccountOwnerFirstName(
									selectedUser.firstName
								);
								setAccountOwnerLastName(selectedUser.lastName);
								setAccountOwnerEmailAddress(
									selectedUser.emailId
								);

								if (
									originalAccountOwnerId !==
									selectedUser.userId
								) {
									setEnableSubmit(true);
								} else {
									setEnableSubmit(false);
								}
								// setAccountOwnerMobileNumber(
								// 	`${user.phoneNumber?.countryCode}${user.phoneNumber?.number}`
								// );
								toggleShowChangeOwnerDialog();
							}}
						></ListUsers>
					</Banner>
				</DialogContent>
			</Dialog>
			{/* 
			<div className="flex items-center justify-center gap-4 p-2 w-fit">
				<div role="status">
					<svg
						aria-hidden="true"
						className="inline w-12 h-12 text-gray-600 animate-spin fill-orange-500"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						/>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						/>
					</svg>
					<span className="sr-only">Loading...</span>
				</div>
				<div role="status">
					<svg
						aria-hidden="true"
						className="inline w-12 h-12 text-gray-600 animate-spin fill-orange-500"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						/>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						/>
					</svg>
					<span className="sr-only">Loading...</span>
				</div>{" "}
				<div role="status">
					<svg
						aria-hidden="true"
						className="inline w-12 h-12 text-gray-600 animate-spin fill-orange-500"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						/>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						/>
					</svg>
					<span className="sr-only">Loading...</span>
				</div>{" "}
				<div role="status">
					<svg
						aria-hidden="true"
						className="inline w-12 h-12 text-gray-600 animate-spin fill-orange-500"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						/>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						/>
					</svg>
					<span className="sr-only">Loading...</span>
				</div>
			</div> */}
		</Banner>
	);
};

export default AccountManagement;
